import { useEffect, useState, useRef } from "react";
import { Search, Plus, Truck, FileText, Edit2, Trash2, ChevronLeft, ChevronRight, CheckCircle, AlertCircle } from "lucide-react";
import Swal from "sweetalert2";
import api from "../services/api";
import SupplierForm from "../components/SupplierForm";

export default function Suppliers() {
  const [suppliers, setSuppliers] = useState([]);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const searchRef = useRef(null);

  // ✅ ESTADOS DE PAGINACIÓN
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [from, setFrom] = useState(0);
  const [to, setTo] = useState(0);

  // ✅ Búsqueda en el backend
  const fetchSuppliers = async (page = 1, searchTerm = search) => {
    try {
      setLoading(true);
      const res = await api.get(`/suppliers/paginated?page=${page}&search=${searchTerm}`);
      
      if (res.data.data) {
        setSuppliers(res.data.data);
        setCurrentPage(res.data.current_page);
        setTotalPages(res.data.last_page);
        setTotal(res.data.total);
        setFrom(res.data.from);
        setTo(res.data.to);
      } else {
        setSuppliers(Array.isArray(res.data) ? res.data : []);
      }
    } catch (error) {
      console.error("Error cargando proveedores:", error);
      Swal.fire("Error", "No se pudieron cargar los proveedores", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSuppliers();

    setTimeout(() => {
      searchRef.current?.focus();
    }, 100);
  }, []);

  // ✅ Función para cambiar página
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      fetchSuppliers(newPage, search);
    }
  };

  // ✅ Manejar búsqueda con debounce
  const handleSearch = (value) => {
    setSearch(value);
    
    // Limpiar timeout anterior
    clearTimeout(window.searchTimeout);
    
    // Buscar después de 500ms de que el usuario dejó de escribir
    window.searchTimeout = setTimeout(() => {
      setCurrentPage(1);
      fetchSuppliers(1, value);
    }, 500);
  };

  // 🗑️ Eliminar (soft delete)
  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "¿Eliminar proveedor?",
      text: "El proveedor se eliminará (soft delete)",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonText: "Cancelar",
      confirmButtonText: "Sí, eliminar",
    });

    if (!result.isConfirmed) return;

    try {
      await api.delete(`/suppliers/${id}`);
      Swal.fire("Eliminado", "Proveedor eliminado correctamente", "success");
      fetchSuppliers(currentPage, search);
    } catch (error) {
      Swal.fire("Error", "No se pudo eliminar el proveedor", "error");
    }
  };

  // ✅ Renderizar badge de estado
  const renderEstadoBadge = (supplier) => {
    const tieneDatos = supplier.tiene_datos_completos;
    const datosIncompletos = supplier.datos_incompletos || [];

    if (tieneDatos) {
      return (
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
            padding: "6px 12px",
            background: "#d1fae5",
            color: "#065f46",
            borderRadius: "6px",
            fontSize: "13px",
            fontWeight: 600,
          }}
          title="Tiene todos los datos fiscales: NIT, NRC y Giro"
        >
          <CheckCircle size={16} />
          Completo
        </div>
      );
    } else {
      const faltantes = datosIncompletos.join(", ");
      return (
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
            padding: "6px 12px",
            background: "#fef3c7",
            color: "#92400e",
            borderRadius: "6px",
            fontSize: "13px",
            fontWeight: 600,
            cursor: "help",
          }}
          title={`Faltan datos: ${faltantes}`}
        >
          <AlertCircle size={16} />
          Incompleto
        </div>
      );
    }
  };

  return (
    <div style={{ padding: "24px", maxWidth: "1400px", margin: "0 auto" }}>
      {/* CARD PRINCIPAL */}
      <div className="card">
        {/* HEADER */}
        <div className="card-header">
          <div className="d-flex align-items-center gap-2">
            <Truck size={28} color="#667eea" />
            <h1 className="card-title">Gestión de Proveedores</h1>
          </div>

          {!showForm && (
            <button
              className="btn btn-success"
              onClick={() => {
                setEditingSupplier(null);
                setShowForm(true);
              }}
            >
              <Plus size={18} />
              Nuevo Proveedor
            </button>
          )}
        </div>

        {/* BODY */}
        <div className="card-body">
          {/* FORMULARIO */}
          {showForm && (
            <SupplierForm
              editingSupplier={editingSupplier}
              onSaved={() => {
                fetchSuppliers(currentPage, search);
                setEditingSupplier(null);
                setShowForm(false);
                setTimeout(() => {
                  searchRef.current?.focus();
                }, 100);
              }}
              onCancel={() => {
                setEditingSupplier(null);
                setShowForm(false);
                setTimeout(() => {
                  searchRef.current?.focus();
                }, 100);
              }}
            />
          )}

          {/* BUSCADOR */}
          {!showForm && (
            <div className="form-group" style={{ position: "relative" }}>
              <div style={{ position: "relative" }}>
                <Search
                  size={18}
                  style={{
                    position: "absolute",
                    left: "16px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "#a0aec0",
                  }}
                />
                <input
                  type="text"
                  className="form-control"
                  placeholder="Buscar por nombre, teléfono o email..."
                  value={search}
                  onChange={(e) => handleSearch(e.target.value)}
                  ref={searchRef}
                  style={{ paddingLeft: "44px" }}
                />
              </div>
              {search && (
                <small style={{ color: "#718096", fontSize: "12px", marginTop: "4px", display: "block" }}>
                  🔍 Buscando en todos los proveedores...
                </small>
              )}
            </div>
          )}

          {/* TABLA */}
          {!showForm && (
            <>
              {loading ? (
                <div className="text-center" style={{ padding: "40px" }}>
                  <div className="spinner" style={{ margin: "0 auto" }}></div>
                  <p style={{ marginTop: "16px", color: "#718096" }}>
                    Cargando proveedores...
                  </p>
                </div>
              ) : (
                <div className="table-container">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Nombre</th>
                        <th>NIT</th>
                        <th>NRC</th>
                        <th>Contacto</th>
                        <th>Teléfono</th>
                        <th style={{ textAlign: "center" }}>Estado</th>
                        <th style={{ textAlign: "center", width: "180px" }}>
                          Acciones
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {suppliers.length === 0 ? (
                        <tr>
                          <td colSpan="7">
                            <div
                              className="text-center"
                              style={{ padding: "40px 20px" }}
                            >
                              <FileText
                                size={48}
                                color="#cbd5e0"
                                style={{ marginBottom: "16px" }}
                              />
                              <p
                                style={{
                                  color: "#a0aec0",
                                  fontSize: "16px",
                                  margin: 0,
                                }}
                              >
                                {search
                                  ? "No se encontraron proveedores con ese criterio"
                                  : "No hay proveedores registrados"}
                              </p>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        suppliers.map((supplier) => (
                          <tr key={supplier.id}>
                            <td>
                              <span style={{ fontWeight: 600 }}>
                                {supplier.nombre}
                              </span>
                            </td>
                            <td>
                              {supplier.nit ? (
                                <strong style={{ fontWeight: 600 }}>
                                  {supplier.nit}
                                </strong>
                              ) : (
                                <span style={{ color: "#cbd5e0" }}>-</span>
                              )}
                            </td>
                            <td>
                              {supplier.nrc ? (
                                <strong style={{ fontWeight: 600 }}>
                                  {supplier.nrc}
                                </strong>
                              ) : (
                                <span style={{ color: "#cbd5e0" }}>-</span>
                              )}
                            </td>
                            <td>{supplier.contacto || "-"}</td>
                            <td>{supplier.telefono || "-"}</td>
                            <td style={{ textAlign: "center" }}>
                              {renderEstadoBadge(supplier)}
                            </td>
                            <td>
                              <div
                                className="d-flex gap-2"
                                style={{ justifyContent: "center" }}
                              >
                                <button
                                  className="btn btn-primary btn-sm"
                                  onClick={() => {
                                    setEditingSupplier(supplier);
                                    setShowForm(true);
                                  }}
                                  title="Editar proveedor"
                                >
                                  <Edit2 size={16} />
                                </button>

                                <button
                                  className="btn btn-danger btn-sm"
                                  onClick={() => handleDelete(supplier.id)}
                                  title="Eliminar proveedor"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {/* ✅ PAGINACIÓN */}
              {totalPages > 1 && (
                <div
                  style={{
                    marginTop: "24px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft size={16} />
                  </button>

                  <span style={{ padding: "6px 12px", color: "#718096", fontSize: "14px" }}>
                    Página {currentPage} de {totalPages}
                  </span>

                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              )}

              {/* RESUMEN */}
              {suppliers.length > 0 && (
                <div
                  style={{
                    marginTop: "16px",
                    padding: "16px",
                    background: "#f7fafc",
                    borderRadius: "10px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span style={{ color: "#718096", fontSize: "14px" }}>
                    Mostrando {from} - {to} de {total} proveedores
                  </span>
                  <div style={{ display: "flex", gap: "16px", alignItems: "center" }}>
                    <span style={{ color: "#718096", fontSize: "14px" }}>
                      <CheckCircle size={16} style={{ display: "inline", color: "#10b981", marginRight: "4px" }} />
                      Completos: <strong style={{ color: "#065f46" }}>
                        {suppliers.filter(s => s.tiene_datos_completos).length}
                      </strong>
                    </span>
                    <span style={{ color: "#718096", fontSize: "14px" }}>
                      <AlertCircle size={16} style={{ display: "inline", color: "#f59e0b", marginRight: "4px" }} />
                      Incompletos: <strong style={{ color: "#92400e" }}>
                        {suppliers.filter(s => !s.tiene_datos_completos).length}
                      </strong>
                    </span>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}